<script setup lang="ts">
import { computed } from 'vue'
import { X } from 'lucide-vue-next'
import type { BaseComponentProps } from '@/types'
import { useI18n } from '@/composables/useI18n'

interface Props extends BaseComponentProps {
  variant?: 'default' | 'gold' | 'primary' | 'success' | 'warning' | 'danger' | 'error' | 'info' | 'gray' | 'live'
  size?: 'sm' | 'md' | 'lg'
  dot?: boolean
  removable?: boolean
  onRemove?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  size: 'md',
  dot: false,
  removable: false,
})

const emit = defineEmits<{
  remove: []
}>()

const { t } = useI18n()

const isLive = computed(() => props.variant === 'live')
// Only `live` sits on a dark/saturated fill, so only it needs the on-ink remove button.
const onInk = computed(() => props.variant === 'live')

const variantClasses = computed(() => {
  const variants: Record<NonNullable<Props['variant']>, string> = {
    default: 'bg-accent text-text-secondary',
    gray: 'bg-accent text-text-muted',
    gold: 'bg-primary/20 text-onPrimaryContainer',
    primary: 'bg-primary/20 text-onPrimaryContainer',
    success: 'bg-success/10 text-success',
    // #D97706 on its own 15% tint is ~3.4:1 — under AA for 11px text. The deep
    // amber `onPrimaryContainer` token (#7A5200) on the same tint reads ~5.9:1.
    warning: 'bg-warning/15 text-onPrimaryContainer',
    danger: 'bg-error/10 text-error',
    error: 'bg-error/10 text-error',
    // A solid ink chip is reserved for selected filters; `info` is an ordinary
    // status (under review, active bid) and must not out-shout its neighbours.
    info: 'bg-ink/10 text-text-primary',
    live: 'bg-success/10 text-success font-semibold',
  }
  return variants[props.variant]
})

const sizeClasses = computed(() => {
  const sizes = {
    sm: 'px-2 py-0.5 text-[11px] gap-1',
    md: 'px-2.5 py-1 text-[11px] gap-1.5',
    lg: 'px-3 py-1.5 text-xs gap-2',
  }
  return sizes[props.size]
})

const removeClasses = computed(() =>
  onInk.value
    ? 'text-white/70 hover:bg-ink-soft hover:text-white'
    : 'hover:bg-ink/10'
)

function handleRemove() {
  if (props.removable) {
    emit('remove')
  }
}
</script>

<template>
  <span
    :class="[
      'inline-flex items-center whitespace-nowrap rounded-full font-semibold transition-colors',
      variantClasses,
      sizeClasses,
      props.class
    ]"
  >
    <span v-if="isLive" class="relative inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-white live-dot" aria-hidden="true" />
    <span v-else-if="props.dot" class="h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-70" aria-hidden="true" />
    <slot />
    <button
      v-if="removable"
      type="button"
      class="-mr-1 ml-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      :class="removeClasses"
      :aria-label="t('aria.remove')"
      @click="handleRemove"
    >
      <X class="h-3 w-3" />
    </button>
  </span>
</template>
